import { useEffect, useRef } from "react";

interface SliderSwipeOptions {
  value: number;
  min: number;
  max: number;
  step: number | "any";
  disabled?: boolean;
  onValueChange: (value: number) => void;
}

/** Horizontal trackpad gestures adjust the range; vertical scroll and pinch stay native. */
export function useSliderSwipe(options: SliderSwipeOptions) {
  const control = useRef<HTMLDivElement>(null);
  const latest = useRef(options);
  latest.current = options;

  useEffect(() => {
    const node = control.current;
    if (!node) return;
    let session: { raw: number; observed: number; emitted: number; time: number; min: number; max: number; step: number | "any" } | null = null;
    const reset = () => { session = null; };
    const wheel = (event: WheelEvent) => {
      const { value, min, max, step, disabled, onValueChange } = latest.current;
      const input = node.querySelector("input");
      if (disabled || input?.matches(":disabled") || event.defaultPrevented || !event.cancelable ||
          event.ctrlKey || event.metaKey || !Number.isFinite(event.deltaX) ||
          !Number.isFinite(event.deltaY) || Math.abs(event.deltaX) <= Math.abs(event.deltaY) || max <= min) {
        reset();
        return;
      }
      const width = node.getBoundingClientRect().width;
      if (width <= 0) return;
      // A non-passive listener prevents horizontal page/history navigation only
      // for a horizontal gesture over this control, including at either limit.
      event.preventDefault();
      const now = performance.now();
      if (!session || now - session.time > 240 || session.min !== min || session.max !== max || session.step !== step ||
          (value !== session.observed && value !== session.emitted)) {
        session = { raw: value, observed: value, emitted: value, time: now, min, max, step };
      }
      const pixels = event.deltaX * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? width : 1);
      const ceiling = step === "any" ? max : Number((min + Math.floor((max - min) / step + 1e-9) * step).toPrecision(12));
      // Reverse wheel deltas so the fill follows the requested Mac swipe direction.
      session.raw = Math.min(ceiling, Math.max(min, session.raw - pixels / width * (max - min)));
      session.observed = value;
      session.time = now;
      // Keep fractional movement between events so fine trackpad deltas can
      // cross a step without accumulating invisible movement beyond a limit.
      const next = step === "any" ? session.raw : Number((min + Math.round((session.raw - min) / step) * step).toPrecision(12));
      if (next !== session.emitted) {
        session.emitted = next;
        onValueChange(next);
      }
    };
    node.addEventListener("wheel", wheel, { passive: false });
    node.addEventListener("pointerdown", reset);
    node.addEventListener("keydown", reset);
    return () => {
      node.removeEventListener("wheel", wheel);
      node.removeEventListener("pointerdown", reset);
      node.removeEventListener("keydown", reset);
    };
  }, []);
  return control;
}
